import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import {
  Clock, CalendarDays, LogIn, MapPin, Fingerprint, CheckCircle2,
  AlertTriangle, FileText, ArrowRight, ShieldCheck, KeyRound, ExternalLink
} from 'lucide-react';

export default function WelcomePage() {
  const [searchParams] = useSearchParams();
  const name = searchParams.get('name') || 'Team Member';
  const email = searchParams.get('email') || '';
  const authType = searchParams.get('auth') || 'google';
  const firstName = name.split(' ')[0];

  const loginUrl = window.location.origin + '/login';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center gap-4">
          <img src="/visaxpress-logo.svg" alt="VisaXpress" className="h-10" />
          <div>
            <h1 className="text-xl font-heading font-bold tracking-tight">VisaXpress Falcon</h1>
            <p className="text-sm text-muted-foreground">Employee Portal</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {/* Welcome Banner */}
        <div className="rounded-xl bg-primary/5 border border-primary/10 p-8" data-testid="welcome-banner">
          <h2 className="text-3xl font-heading font-bold tracking-tight mb-2">
            Welcome to Falcon, {firstName}!
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl">
            This is your quick-start guide to the VisaXpress employee portal. Here you'll learn how to log in, record your attendance, and request time off.
          </p>
          {email && (
            <div className="mt-4 flex items-center gap-2">
              <Badge variant="outline" className="text-sm py-1 px-3 gap-1.5">
                {authType === 'password' ? <KeyRound size={12} /> : <ShieldCheck size={12} />}
                {email}
              </Badge>
            </div>
          )}
        </div>

        {/* Step 1: Login */}
        <Card data-testid="guide-login">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <CardTitle className="font-heading">Log In to Falcon</CardTitle>
                <CardDescription>Access the portal using your credentials</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {authType === 'google' ? (
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                  <ShieldCheck className="text-primary mt-0.5 shrink-0" size={20} />
                  <div>
                    <p className="font-medium">Google Single Sign-On (SSO)</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Your account uses Google SSO. Click <strong>"Sign in with Google"</strong> on the login page and use your company Google account (<strong>{email}</strong>).
                    </p>
                  </div>
                </div>
                <ol className="list-decimal list-inside space-y-2 text-sm pl-2">
                  <li>Go to the login page (link below)</li>
                  <li>Click the <strong>"Google SSO"</strong> tab (selected by default)</li>
                  <li>Click <strong>"Sign in with Google"</strong></li>
                  <li>Select your company Google account</li>
                  <li>You'll be redirected to your dashboard</li>
                </ol>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                  <KeyRound className="text-primary mt-0.5 shrink-0" size={20} />
                  <div>
                    <p className="font-medium">Email & Password Login</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Your account uses email and password authentication. Use the credentials provided by your manager to log in.
                    </p>
                  </div>
                </div>
                <ol className="list-decimal list-inside space-y-2 text-sm pl-2">
                  <li>Go to the login page (link below)</li>
                  <li>Click the <strong>"Email & Password"</strong> tab</li>
                  <li>Enter your email: <strong>{email}</strong></li>
                  <li>Enter the password provided by your administrator</li>
                  <li>Click <strong>"Sign In"</strong></li>
                </ol>
              </div>
            )}
            <a href={loginUrl} target="_blank" rel="noopener noreferrer">
              <Button className="gap-2 mt-2" data-testid="login-link-button">
                <LogIn size={16} /> Go to Login Page <ExternalLink size={14} />
              </Button>
            </a>
          </CardContent>
        </Card>

        {/* Step 2: Check In/Out */}
        <Card data-testid="guide-checkin">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <CardTitle className="font-heading">Daily Check-In & Check-Out</CardTitle>
                <CardDescription>Record your attendance every working day</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                <div className="flex items-center gap-2 font-medium">
                  <Clock size={16} className="text-primary" /> Check-In
                </div>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>Navigate to <strong>"Check In/Out"</strong> from the sidebar</li>
                  <li>Allow <strong>location access</strong> when prompted by your browser</li>
                  <li>Click <strong>"Check In"</strong></li>
                  <li>Your time, location, and device will be recorded</li>
                </ol>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                <div className="flex items-center gap-2 font-medium">
                  <CheckCircle2 size={16} className="text-primary" /> Check-Out
                </div>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>At the end of your day, go to <strong>"Check In/Out"</strong></li>
                  <li>Click <strong>"Check Out"</strong></li>
                  <li>Your work hours will be calculated automatically</li>
                </ol>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <p className="font-medium text-sm">Important Punctuality Rules:</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="flex items-start gap-2 text-sm p-3 rounded-lg border">
                  <Fingerprint size={16} className="text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Device Tracking</p>
                    <p className="text-muted-foreground text-xs">Your device fingerprint, IP address, and GPS location are recorded</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 text-sm p-3 rounded-lg border">
                  <AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">15-Minute Grace</p>
                    <p className="text-muted-foreground text-xs">Arriving more than 15 min late results in a half-day deduction</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 text-sm p-3 rounded-lg border">
                  <CalendarDays size={16} className="text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">2 Grace Days/Month</p>
                    <p className="text-muted-foreground text-xs">First 2 late arrivals per month do not trigger a half-day deduction</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 3: Leave Requests */}
        <Card data-testid="guide-leaves">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <CardTitle className="font-heading">Requesting Leave</CardTitle>
                <CardDescription>How to apply for time off</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="list-decimal list-inside space-y-2 text-sm pl-2">
              <li>Go to <strong>"Leaves"</strong> from the sidebar menu</li>
              <li>Click <strong>"Request Leave"</strong></li>
              <li>Select a <strong>leave type</strong>, start date, end date, and reason</li>
              <li>Submit the request — it will go to your <strong>Branch Manager</strong> for approval</li>
              <li>If the leave exceeds certain thresholds, it will be <strong>escalated to the Super User</strong> for final approval</li>
              <li>You can track the status of your requests on the Leaves page</li>
            </ol>

            <Separator />

            <div className="space-y-3">
              <p className="font-medium text-sm">Leave Policy Summary (2026):</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="p-3 rounded-lg border space-y-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm">Annual Leave</p>
                    <Badge variant="default" className="text-xs">15 days</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">Eligible after 6 months of employment. Up to 3 days carry forward to next year.</p>
                </div>
                <div className="p-3 rounded-lg border space-y-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm">Compensatory Off</p>
                    <Badge variant="outline" className="text-xs">Earned</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">For working on public holidays or beyond stipulated hours. Claim within 30 days.</p>
                </div>
                <div className="p-3 rounded-lg border space-y-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm">Leave Without Pay</p>
                    <Badge variant="outline" className="text-xs">By Approval</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">For exceptional circumstances. Subject to managerial discretion.</p>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm space-y-1">
                <p className="font-medium text-amber-800">Key Rules to Remember:</p>
                <ul className="list-disc list-inside text-amber-700 space-y-0.5 text-xs">
                  <li>Maximum <strong>3 consecutive leave days</strong> at a time (exceptions require management approval)</li>
                  <li>Apply at least <strong>10 days in advance</strong> for planned leave</li>
                  <li>Early departures must be informed <strong>2 days in advance</strong> (except emergencies)</li>
                  <li>Unauthorized absences are a <strong>serious violation</strong> and attract disciplinary measures</li>
                  <li>If you leave before completing 1 year, annual leave taken will be deducted from final pay</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2026 Holiday Calendar */}
        <Card data-testid="guide-holidays">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">4</div>
              <div>
                <CardTitle className="font-heading">2026 Holiday Calendar</CardTitle>
                <CardDescription>Public holidays for Delhi Office</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-2">
              {[
                { date: 'Jan 01', name: 'New Year' },
                { date: 'Jan 13', name: 'Lohri' },
                { date: 'Jan 26', name: 'Republic Day' },
                { date: 'Mar 04', name: 'Holi' },
                { date: 'Apr 03', name: 'Good Friday' },
                { date: 'Aug 15', name: 'Independence Day' },
                { date: 'Aug 28', name: 'Raksha Bandhan' },
                { date: 'Oct 02', name: 'Gandhi Jayanti' },
                { date: 'Oct 20', name: 'Dussehra' },
                { date: 'Oct 29', name: 'Karva Chauth' },
                { date: 'Nov 09', name: 'Diwali' },
                { date: 'Nov 24', name: 'Guru Nanak Jayanti' },
                { date: 'Dec 25', name: 'Christmas Day' },
              ].map((h, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border text-sm hover:bg-muted/50 transition-colors">
                  <span className="font-mono text-xs font-medium text-muted-foreground w-14 shrink-0">{h.date}</span>
                  <span>{h.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Need Help */}
        <div className="rounded-xl border p-6 text-center space-y-2">
          <p className="text-muted-foreground">Need help? Contact your Branch Manager or the Super Admin at <strong>info@visaxpress.ca</strong></p>
          <a href={loginUrl} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="gap-2 mt-2" data-testid="welcome-login-button">
              Get Started <ArrowRight size={16} />
            </Button>
          </a>
        </div>

        <p className="text-center text-xs text-muted-foreground pb-8">
          VisaXpress Falcon &copy; {new Date().getFullYear()}
        </p>
      </main>
    </div>
  );
}
